import { useEffect, useState, type ReactNode } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getClient, updateClient } from "../api/clients";
import type { Client } from "../types/client";
import { FileUpload } from "../components/FileUpload";
import { ReceiptCapture } from "../components/ReceiptCapture";
import { uploadBankFile } from "../api/transactions";
import { TransactionList } from "../components/TransactionList";
import { ErrorMessage } from "../components/ErrorMessage";
import { BankAccountSelector } from "../components/BankAccountSelector";
import { PageTransition } from "../components/ui/PageTransition";
import { AnimatedButton } from "../components/ui/AnimatedButton";
import { AccountsPanel } from "../components/accounts/AccountsPanel";
import { ClientBreadcrumb, ClientPageHeader } from "../components/ClientPageHeader";
import { BusinessHeaderCard, type BusinessEdits } from "../components/BusinessHeaderCard";

export function ClientDetailPage(): ReactNode {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [bankAccount, setBankAccount] = useState("");

  const numericId = clientId ? parseInt(clientId, 10) : NaN;

  useEffect(() => {
    if (isNaN(numericId)) {
      setError("Invalid business ID");
      setLoading(false);
      return;
    }

    async function load(): Promise<void> {
      try {
        const data = await getClient(numericId);
        setClient(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Business not found");
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, [numericId]);

  function handleUploadComplete(): void {
    setRefreshKey((prev) => prev + 1);
  }

  const [demoLoading, setDemoLoading] = useState(false);

  async function loadDemoStatement(): Promise<void> {
    if (!client) return;
    setDemoLoading(true);
    const ofx = `<?xml version="1.0" encoding="UTF-8"?>
<?OFX OFXHEADER="200" VERSION="220" SECURITY="NONE" OLDFILEUID="NONE" NEWFILEUID="NONE"?>
<OFX><SIGNONMSGSRSV1><SONRS><STATUS><CODE>0</CODE><SEVERITY>INFO</SEVERITY></STATUS>
<DTSERVER>20260401120000</DTSERVER><LANGUAGE>ENG</LANGUAGE></SONRS></SIGNONMSGSRSV1>
<BANKMSGSRSV1><STMTTRNRS><TRNUID>DEMO1</TRNUID><STATUS><CODE>0</CODE><SEVERITY>INFO</SEVERITY></STATUS>
<STMTRS><CURDEF>USD</CURDEF><BANKACCTFROM><BANKID>121000358</BANKID><ACCTID>999888777</ACCTID>
<ACCTTYPE>CHECKING</ACCTTYPE></BANKACCTFROM><BANKTRANLIST><DTSTART>20260301</DTSTART><DTEND>20260401</DTEND>
<STMTTRN><TRNTYPE>DEBIT</TRNTYPE><DTPOSTED>20260315</DTPOSTED><TRNAMT>-31.81</TRNAMT><FITID>DEMO001</FITID><NAME>HOME DEPOT #4521</NAME></STMTTRN>
<STMTTRN><TRNTYPE>DEBIT</TRNTYPE><DTPOSTED>20260318</DTPOSTED><TRNAMT>-46.54</TRNAMT><FITID>DEMO002</FITID><NAME>OLIVE GARDEN #2847</NAME></STMTTRN>
<STMTTRN><TRNTYPE>DEBIT</TRNTYPE><DTPOSTED>20260320</DTPOSTED><TRNAMT>-89.99</TRNAMT><FITID>DEMO003</FITID><NAME>COSTCO WHSE #1234</NAME></STMTTRN>
<STMTTRN><TRNTYPE>DEBIT</TRNTYPE><DTPOSTED>20260322</DTPOSTED><TRNAMT>-12.50</TRNAMT><FITID>DEMO004</FITID><NAME>STARBUCKS #9876</NAME></STMTTRN>
<STMTTRN><TRNTYPE>DEBIT</TRNTYPE><DTPOSTED>20260325</DTPOSTED><TRNAMT>-250.00</TRNAMT><FITID>DEMO005</FITID><NAME>SHELL OIL 57432</NAME></STMTTRN>
<STMTTRN><TRNTYPE>CREDIT</TRNTYPE><DTPOSTED>20260328</DTPOSTED><TRNAMT>3500.00</TRNAMT><FITID>DEMO006</FITID><NAME>CLIENT PAYMENT - JOHNSON</NAME></STMTTRN>
<STMTTRN><TRNTYPE>DEBIT</TRNTYPE><DTPOSTED>20260330</DTPOSTED><TRNAMT>-74.23</TRNAMT><FITID>DEMO007</FITID><NAME>LOWES #0542</NAME></STMTTRN>
</BANKTRANLIST><LEDGERBAL><BALAMT>5245.93</BALAMT><DTASOF>20260401</DTASOF></LEDGERBAL>
</STMTRS></STMTTRNRS></BANKMSGSRSV1></OFX>`;
    try {
      const blob = new Blob([ofx], { type: "application/x-ofx" });
      const file = new File([blob], "demo-statement.ofx", { type: "application/x-ofx" });
      await uploadBankFile(client.id, file);
      handleUploadComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load demo");
    } finally {
      setDemoLoading(false);
    }
  }

  async function saveEdits(edits: BusinessEdits): Promise<void> {
    if (!client) return;
    setClient(await updateClient(client.id, edits));
  }

  if (loading) {
    return (
      <main className="client-detail-page">
        <div className="loading-state">
          <div className="loading-spinner" />
          Loading business details...
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="client-detail-page">
        <ErrorMessage message={error} />
        <AnimatedButton type="button" onClick={() => navigate("/clients")}>
          Back to businesses
        </AnimatedButton>
      </main>
    );
  }

  if (!client) {
    return null;
  }

  return (
    <main className="client-detail-page">
      <ClientPageHeader onBack={() => navigate("/clients")} />

      <PageTransition>
        <ClientBreadcrumb clientName={client.business_name} />

        <BusinessHeaderCard client={client} onSave={saveEdits} />

        <p className="client-page-links">
          <Link to={`/clients/${client.id}/numbers`}>Numbers</Link>
        </p>

        <div className="section-card">
          <section className="upload-section">
            <h2>Import transactions</h2>
            <BankAccountSelector value={bankAccount} onChange={setBankAccount} />
            <FileUpload
              clientId={client.id}
              bankAccount={bankAccount}
              onUploadComplete={handleUploadComplete}
            />
          </section>
        </div>

        <div className="section-card">
          <AccountsPanel
            clientId={client.id}
            refreshKey={refreshKey}
            onChanged={handleUploadComplete}
          />
        </div>

        <div className="section-card">
          <section className="receipt-section">
            <h2>Capture a receipt</h2>
            <ReceiptCapture clientId={client.id} onUploadComplete={handleUploadComplete} />
          </section>
        </div>

        <div className="section-card">
          <section>
            <button
              onClick={loadDemoStatement}
              disabled={demoLoading}
              style={{
                padding: "10px 20px",
                borderRadius: "8px",
                border: "1px solid var(--color-border, #d1d5db)",
                background: "var(--color-surface, #f9fafb)",
                cursor: demoLoading ? "wait" : "pointer",
                fontSize: "0.9rem",
              }}
            >
              {demoLoading ? "Loading..." : "Load demo transactions"}
            </button>
          </section>
        </div>

        <div className="section-card">
          <section className="transactions-section">
            <h2>Review transactions</h2>
            <TransactionList clientId={client.id} refreshKey={refreshKey} />
          </section>
        </div>
      </PageTransition>
    </main>
  );
}
